// Страница подтверждения согласия на обработку персональных данных (152-ФЗ).
// Показывается после успешного логина (и смены пароля, если был обязательным),
// если pdn_accepted_at = null. Без согласия пользователь не может пройти дальше.
//
// UX:
//   - Краткий текст что собираем + ссылка на /policy с полной политикой
//   - Чекбокс «Я ознакомился и согласен»
//   - Кнопка «Принимаю» активна только при чекбоксе
//   - «Войти под другим аккаунтом» — logout + редирект на /login

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Paper,
    Title,
    Text,
    Stack,
    Button,
    Checkbox,
    Alert,
    Anchor,
    Group,
} from '@mantine/core';
import { useAuth } from '../auth/AuthContext.jsx';
import { authApi } from '../api/auth.js';
import { ApiError } from '../api/client.js';

export default function AcceptPdnPage() {
    const { refresh, logout } = useAuth();
    const navigate = useNavigate();

    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const onSubmit = async () => {
        if (!accepted || submitting) return;
        setError(null);
        setSubmitting(true);
        try {
            await authApi.acceptPdn();
            await refresh();
            navigate('/', { replace: true });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Не удалось сохранить согласие');
            setSubmitting(false);
        }
    };

    const onCancel = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    return (
        <Paper p="xl" radius="md" withBorder shadow="sm">
            <Stack gap="md">
                <div>
                    <Title order={2}>Согласие на обработку данных</Title>
                    <Text size="sm" c="dimmed" mt={4}>
                        Чтобы пользоваться сервисом, нужно подтвердить согласие на обработку
                        персональных данных (152-ФЗ).
                    </Text>
                </div>

                <Text size="sm">
                    Мы храним: ваш логин и ФИО (для входа), текстовые транскрипты учебных
                    диалогов и оценки от AI-РОПа (по умолчанию 30 дней). Аудиозаписи
                    разговоров не сохраняются. Голосовое общение и автоматический разбор
                    обрабатываются через Gemini API (Google).
                </Text>

                <Text size="sm">
                    Подробнее — в{' '}
                    <Anchor component={Link} to="/policy" target="_blank">
                        Политике конфиденциальности
                    </Anchor>
                    .
                </Text>

                <Checkbox
                    label="Я ознакомился(-ась) с Политикой и согласен(-на) на обработку моих персональных данных"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.currentTarget.checked)}
                    disabled={submitting}
                />

                {error && (
                    <Alert color="red" variant="light">
                        {error}
                    </Alert>
                )}

                <Group justify="space-between" mt="sm">
                    <Anchor
                        component="button"
                        type="button"
                        size="sm"
                        onClick={onCancel}
                        disabled={submitting}
                    >
                        Войти под другим аккаунтом
                    </Anchor>
                    <Button
                        onClick={onSubmit}
                        disabled={!accepted}
                        loading={submitting}
                    >
                        Принимаю
                    </Button>
                </Group>
            </Stack>
        </Paper>
    );
}
